export interface CancelCondition {
	name?: string;
	day: number;
	hour: number;
	minute: number;
	refundPercentage?: number;
}

export interface EventOccurrence {
	occurrenceId: number;
	eventOccasionId: number;
	startAt: string;
	endAt: string;
	maxAttendees: number;
	sumExpected: number;
	sumAttended: number;
	remarks?: string | null;
	isDisplayed?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface EventDetailData {
	eventOccasionId: number;
	title: string;
	subtitle?: string | null;
	description?: string | null;
	heroImageUrl?: string | null;
	gallery?: string[];
	isPublished: boolean;
	reservationCount: number;
	attendeeCount: number;
	maximumAttendees: number;
	occurrences: EventOccurrence[];
	categoryStart?: string | null;
	categoryEnd?: string | null;
	occasionImages?: Array<{ picUrl: string; showOrder: number }>;
	fee?: number;
	price?: number | string | null;
	cancelable?: boolean;
	isMultiEvent?: boolean;
	allowMultipleBookings?: boolean;
	isMessage?: boolean;
	isCheckin?: boolean;
	membershipScope?: "member_only" | "member_and_guest";
	categoryMessageDetail?: {
		afterReservationMessage?: string;
		reminderMessageSevenDays?: string;
		reminderMessageOneDay?: string;
	};
	cancelConditions?: CancelCondition[];
	cancelDescription?: string | null;
	bookCancelMessage?: string | null;
	isDisplayed?: boolean;
	notRegisterEventSameTime?: boolean;
	isProgram?: boolean;
	adminNotificationMemberIds?: string | number[] | null;
}

export interface ManualReservationPayload {
	occurrenceId: number;
	lastName: string;
	firstName: string;
	lastNameKana: string;
	firstNameKana: string;
	telephone: string;
	email?: string;
	postalCode?: string;
	address?: string;
	building?: string;
	message?: string;
}

export interface CreateOccurrencePayload {
	eventOccasionId: number;
	templateSlots: Array<{
		maxAttendees: number;
		startAt: string;
		endAt: string;
		minAttendees?: number;
		bufferWaitlistSize?: number;
		isDisplayed?: boolean;
	}>;
}

export interface DeleteOccurrencesPayload {
	occurrenceIds: number[];
}

export interface ReservationExportParams {
	eventOccasionId: number;
	startDate?: string;
	endDate?: string;
	format?: "csv" | "excel";
}

export type SlotDuration = 15 | 30 | 60 | 90 | 120 | 180 | 480;

export interface DayTimeRange {
	startTime: string;
	endTime: string;
}

export interface DayOfWeek {
	value: number;
	label: string;
	longLabel: string;
	enabled: boolean;
	times: DayTimeRange[];
}

export const DAYS_OF_WEEK: DayOfWeek[] = [
	{
		value: 7,
		label: "日",
		longLabel: "日曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 1,
		label: "月",
		longLabel: "月曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 2,
		label: "火",
		longLabel: "火曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 3,
		label: "水",
		longLabel: "水曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 4,
		label: "木",
		longLabel: "木曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 5,
		label: "金",
		longLabel: "金曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
	{
		value: 6,
		label: "土",
		longLabel: "土曜日",
		enabled: true,
		times: [{ startTime: "", endTime: "" }],
	},
];

export const SLOT_DURATION_OPTIONS: Array<{
	value: number;
	label: string;
}> = [
	{ value: 15, label: "15分" },
	{ value: 30, label: "30分" },
	{ value: 60, label: "1時間" },
	{ value: 90, label: "1時間30分" },
	{ value: 120, label: "2時間" },
	{ value: 180, label: "3時間" },
	{ value: 480, label: "8時間" },
];

export const CALENDAR_SLOT_DURATION_OPTIONS = SLOT_DURATION_OPTIONS;

export interface MemberAttribute {
	id: number | string;
	name: string;
	type: string;
	value: string | null;
}

export interface MemberDetail {
	memberId?: number;
	memberCode?: string | null;

	displayName?: string | null;
	picUrl?: string | null;
	lastName?: string | null;
	firstName?: string | null;
	lastNameKana?: string | null;
	firstNameKana?: string | null;
	telephone?: string | null;
	email?: string | null;
	postalCode?: string | null;
	prefecture?: string | null;
	city?: string | null;
	address?: string | null;
	building?: string | null;
	attributes?: MemberAttribute[];
}

export interface ChatMessage {
	id: number;
	chatId?: number;
	memberId: number;
	contents: string;
	contentType?: "text" | "image";
	source: "user" | "admin" | "system";
	createdAt: string;
}

export interface UpdateRegistrationPayload {
	lastName?: string;
	firstName?: string;
	lastNameKana?: string;
	firstNameKana?: string;
	telephone?: string;
	email?: string;
	postalCode?: string;
	address?: string;
	building?: string;
	message?: string;
	remarks?: string;
}

export interface UpdateOccurrencePayload {
	maxAttendees?: number;
	remarks?: string;
	isDisplayed?: boolean;
	startAt?: string;
	endAt?: string;
}

export interface UpdateRegistrationStatusPayload {
	status: string;
	paymentStatus?: string;
	paymentMethod?: string;
}

export interface UpdateAttendancePayload {
	primaryParticipantCount: number;
	companionCount: number;
	checkedInAt?: string;
	checkInSource?: string;
	checkedInBy?: number;
}

export interface Registration {
	registrationId: number;
	memberId?: number;
	chatMemberId?: number;
	lineUserId?: string;
	nameFull?: string;
	nameKana?: string;
	liveLineName?: string;
	liveMemberName?: string;
	memberName?: string;
	displayName?: string;
	lastName?: string;
	firstName?: string;
	lastNameKana?: string;
	firstNameKana?: string;
	telephone?: string;
	email?: string;
	postalCode?: string;
	address?: string;
	building?: string;
	expected?: number;
	attended?: number;
	participantCount?: number;
	actualParticipantCount?: number;
	companionCount?: number;
	actualCompanionCount?: number;
	isManual?: boolean;
	message?: string;
	remarks?: string;
	createdAt?: string;
	cancelledAt?: string;
	paymentAmount?: number;
	paymentOrderId?: string;
	paymentStatus?: string;
	paymentMethod?: string;
	checkedIn?: boolean;
	membershipStatus?: string;
}
